import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { SupplierCardData } from '../company/common/SupplierCard';
import InfoSocialTags from '../social-proof/InfoSocialTags';
import { formatCompanyAddress, getDeliveryZoneFromAdministrativeArea, getMinimumOrderValueFromDeliveryZones } from '../common/utils/common.utils';
import { PackagingFamilyTypeIntl } from '../common/i18n/product-family.translation';
import { MinimumOrderValue } from '../common/state/state';
import FileUtils from '../common/utils/file.utils';
import { ellipsisStyle, linkStyle } from '../common/utils/style.utils';
import { currentContextAtom, supplierSheetSelectedCompanyIdAtom } from '../state';
import SocialTags from '../social-proof/SocialTags';
import DiscoveryDialogPackagings from '../discovery/as-buyer/DiscoveryDialogPackagings';
import WishlistContactButton from './WishlistContactButton';
import WishlistSupplierButton from './WishlistSupplierButton';

const IMAGE_WIDTH = 198;

interface WishlistSupplierRowProps {
  supplier: SupplierCardData;
}

export default function WishlistSupplierRow({ supplier }: WishlistSupplierRowProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'product-family']);
  const { companyAddress } = useAtomValue(currentContextAtom);
  const setSelectedCompanyId = useSetAtom(supplierSheetSelectedCompanyIdAtom);

  const handleSupplierClick = (): void => {
    setSelectedCompanyId(supplier.id);
  };

  const minimumOrderValue: MinimumOrderValue | undefined = useMemo(() => {
    return getMinimumOrderValueFromDeliveryZones(
      supplier.deliveryZones,
      companyAddress.administrativeAreaLevel2 || '',
    )
  }, [supplier.deliveryZones, companyAddress.administrativeAreaLevel2]);

  const deliveryTime = useMemo((): number | undefined => {
    const deliveryZone = getDeliveryZoneFromAdministrativeArea(
      supplier.deliveryZones,
      companyAddress.administrativeAreaLevel2 || '',
    );

    return deliveryZone?.delivery_time || undefined;
  }, [supplier.deliveryZones, companyAddress.administrativeAreaLevel2]);

  return (
    <Box sx={{ display: 'flex', gap: 3, width: '100%', alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', gap: 3, width: 520, flexShrink: 0, flex: 1 }}>
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            bgcolor: 'white',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={handleSupplierClick}
            sx={{ display: 'block', width: IMAGE_WIDTH, cursor: 'pointer' }}
          >
            <Box
              component="img"
              src={FileUtils.BuildCompanyImageUrl({
                photo: supplier.photo,
                type: supplier.businessProfile,
                size: { width: IMAGE_WIDTH, height: IMAGE_WIDTH, fit: 'cover' },
              })}
              sx={{
                width: IMAGE_WIDTH,
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
          <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 5 }}>
            <WishlistSupplierButton supplierId={supplier.id} />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            pt: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box onClick={handleSupplierClick} sx={{ ...linkStyle, cursor: 'pointer' }}>
              <Typography variant="titleMedium" sx={ellipsisStyle}>
                {supplier.tradeName}
              </Typography>
            </Box>
            {!!supplier.productFamilies.length && (
              <Typography variant="bodySmall" color="secondary">
                {t(`product-family:${PackagingFamilyTypeIntl[supplier.productFamilies[0]]}`)}
                {supplier.productFamilies.length > 1 && ` (+${supplier.productFamilies.length - 1})`}
              </Typography>
            )}
          </Box>
          {supplier.address?.locality && supplier.address?.postalCode && (
            <Typography variant="bodySmall">
              {formatCompanyAddress(supplier.address)}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <InfoSocialTags
              minimumOrderValue={minimumOrderValue}
              isWholesaler={supplier.isWholesaler}
              targetGeoLoc={{
                lat: supplier.address!.latitude,
                lng: supplier.address!.longitude,
              }}
              deliveryTime={deliveryTime}
            />
          </Box>
          {!!supplier.socialTags.length && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <SocialTags tags={supplier.socialTags} displayRules={{ rules: { 1: 1, 2: 1 } }} />
            </Box>
          )}
        </Box>
      </Box>
      <DiscoveryDialogPackagings companyId={supplier.id} variant="wishlist" />
      <Box sx={{ pt: 1, flexShrink: 0 }}>
        <WishlistContactButton supplierId={supplier.id} />
      </Box>
    </Box>
  );
}
